import { Sparkles } from "lucide-react";

/**
 * Print-directory style college profile — v3
 * Route: /design-sample/college-profile-v3
 * Inspired by the "Higher Education Directory" print reference.
 * NOT live. Pure preview for stakeholder approval.
 */

/* Palette — warm print cream / ink black / brand red */
const CREAM = "#F5EFDF";
const INK = "#0B0B0B";
const RED = "#7A1F1F";
const RULE = "#1A1A1A";

/* ---------- Sample content ---------- */
const college = {
  name: "Chetana's Institute of Management & Research",
  short: "CIMR",
  accreditation: "AICTE New Delhi Approved · 2018 Certified",
  address_lines: [
    "Survey No 341, Government Colony,",
    "Bandra East.",
    "Mumbai · 400051, Maharashtra",
  ],
  phone: "9321478440 / 9326966204",
  email: ["director@cimr.in", "info@cimr.in"],
  website: "www.cimr.in",
  director: "Dr. Nandita Mishra",
  courses_pg: ["Post-Graduate Diploma in Management", "PGDM Marketing Program"],
  courses_doc: ["Fellow Program in Management"],
  facilities: "Library Books, Online Knimbus Platform, Training and Development, Laptops are given to each student, IT Lab and software available for students, International Immersion Program, Industrial Visit, Personality Development and Corporate Grooming.",
  logo: "https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?auto=format&fit=crop&w=200&q=80",
  photos: {
    building: "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=900&q=80",
    office_1:  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80",
    library:   "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=900&q=80",
    hall:      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=900&q=80",
    lab:       "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=900&q=80",
    group:     "https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&w=1600&q=80",
  },
  paras_left: [
    "The legacy of its Founder President, Late Shri Madhukarrao Chaudhari, continues to be upheld by his son, Shri Shirish Chaudhari, Secretary of Chetana Trust. His constant support and guidance, along with the tireless effort and leadership of his daughter, Dr Madhumita Patil, the CEO has helped CIMR achieve every milestone. The management committee endeavours to educate students from both rural and urban areas, preparing them to tackle real-life challenges and contribute to building a healthy and self-reliant society.",
    "Chetana's Institute of Management Research (CIMR), celebrating 28 glorious years, has been imparting quality management education programs to meet the industry requirements. The NBA accreditation of the PGDM & PGDM Marketing programs along with MBA Equivalence by AIU, reflects the commitment to quality education.",
    "The Fellow Program in Management (Doctoral Program) was launched in 2023 for practicing professionals.",
    "Academic Programs at Chetana's are designed to cater to the increasingly complex skills required in today's globalized world. Empowering the students with a combination of contemporary pedagogy and best practices across various industries is the core of \u201Cexperiential learning\u201D.",
  ],
  paras_left_bottom: [
    { heading: "Creating global awareness in our programs", body: "Chetana's Institute of Management and Research (CIMR), a leader in Management Education, has played a pivotal role since its inception in 1996 to build a strong foundation for management education in India. Beyond the traditional classroom setting, Chetana provides hands-on learning experiences through functional labs, simulations, and workshops. Internships, consulting projects," },
  ],
  paras_right: [
    "Industrial visits provide invaluable opportunities for students to gain practical skills, build networks, and explore the local and national business environments. Innovation and entrepreneurship labs provide platforms for students to develop and launch their business ideas, transforming ideas into new ventures.",
    "Chetana's Institute of Management & Research has vividly distinguished itself while grooming the students for the industry through, off-summer's, summer internships and live projects leading to executive placements with reputed brands.",
    "CIMR has courses on ESG and sustainability to address the shift in focus towards integrating profits with people and the planet. By aligning academic activities with the UN Sustainable Development Goals (SDGs), students are motivated to engage with the community and undertake projects that contribute to overall social development. This approach encourages students to engage in ESG activities in collaboration with industries, NGOs, SHGs, and governmental bodies. Furthermore, by integrating SDGs into credit courses, research, and project work, key competencies are developed, thereby enhancing interpersonal skills as ambassadors for sustainability in their future professional endeavours.",
  ],
};

/* ---------- Small components ---------- */
const P = ({ children }) => (
  <p style={{
    textAlign: "justify",
    hyphens: "auto",
    marginBottom: "0.75rem",
    fontFamily: "'Merriweather', 'PT Serif', Georgia, serif",
    fontSize: 12.5,
    lineHeight: 1.55,
    color: INK,
  }}>{children}</p>
);

const Photo = ({ src, ratio = "4/3", label }) => (
  <div className="mb-3" style={{ aspectRatio: ratio, overflow: "hidden", border: `1px solid ${RULE}` }}>
    <img src={src} alt={label || ""} className="w-full h-full object-cover"/>
  </div>
);

const Heading = ({ children }) => (
  <div style={{
    fontFamily: "'Merriweather', 'PT Serif', Georgia, serif",
    fontWeight: 700,
    fontSize: 13,
    color: INK,
    marginBottom: "0.35rem",
  }}>{children}</div>
);

/* ---------- Page ---------- */
export default function CollegeProfileSampleV3() {
  return (
    <div data-testid="college-profile-v3" style={{ background: CREAM, minHeight: "100vh" }}>
      {/* Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700;900&family=PT+Serif:wght@400;700&display=swap');
        .print-page { font-family: 'PT Serif', Georgia, serif; }
      `}</style>

      {/* Sample ribbon */}
      <div className="fixed bottom-5 right-5 z-50 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold shadow-lg flex items-center gap-2 rounded-full" style={{ background: RED, color: CREAM }} data-testid="sample-ribbon">
        <Sparkles className="w-3.5 h-3.5"/> Sample preview · v3
      </div>

      <div className="print-page max-w-[1100px] mx-auto px-4 md:px-10 py-8">
        {/* Top decorative rule */}
        <div style={{ height: 1, background: RULE, marginBottom: 24 }}/>

        {/* Title bar */}
        <div className="flex justify-center">
          <div style={{
            background: INK, color: "#FFFFFF",
            padding: "14px 28px",
            fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 22,
            letterSpacing: 0.2,
            maxWidth: "78%",
          }} data-testid="title-bar">
            {college.name}
          </div>
        </div>

        {/* 3-column magazine layout */}
        <div className="grid md:grid-cols-[1.05fr_1fr_1.05fr] gap-6 mt-8" data-testid="magazine-columns">
          {/* ============ LEFT COLUMN ============ */}
          <div>
            <Photo src={college.photos.building} ratio="3/4" label="Building"/>
            {college.paras_left.map((t, i) => <P key={i}>{t}</P>)}
          </div>

          {/* ============ CENTER COLUMN ============ */}
          <div className="flex flex-col">
            {/* two photos on top */}
            <Photo src={college.photos.office_1} ratio="4/3" label="Office"/>
            <Photo src={college.photos.library} ratio="4/3" label="Library"/>

            {/* bordered contact card */}
            <div style={{ border: `1.5px solid ${RED}`, padding: "18px 20px", background: CREAM }} data-testid="contact-card">
              <div className="flex items-center gap-2 mb-3">
                <div style={{
                  width: 26, height: 26, border: `1.5px solid ${RED}`,
                  borderRadius: "50%", display: "grid", placeItems: "center",
                  color: RED, fontFamily: "'Merriweather', serif", fontWeight: 900, fontSize: 12,
                }}>C</div>
                <div>
                  <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, fontSize: 13, color: INK }}>Chetana's</div>
                  <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 10.5, color: INK }}>Institute of Management & Research</div>
                  <div style={{ fontSize: 8, color: INK, fontStyle: "italic" }}>{college.accreditation}</div>
                </div>
              </div>
              <div style={{ height: 1, background: RULE, opacity: 0.15, margin: "12px 0" }}/>

              <div className="text-center" style={{ fontFamily: "'Merriweather', serif", fontSize: 12.5, color: INK }}>
                <div style={{ fontWeight: 700 }}>Chetana's Institute of Management</div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>& Research</div>
                {college.address_lines.map((l, i) => <div key={i} style={{ fontSize: 12 }}>{l}</div>)}
              </div>

              <div style={{ height: 12 }}/>
              <div className="text-center">
                <div style={{ fontWeight: 700, fontSize: 12.5, fontFamily: "'Merriweather', serif" }}>Tel:</div>
                <div style={{ fontSize: 12 }}>{college.phone}</div>
              </div>
              <div style={{ height: 12 }}/>
              <div className="text-center">
                <div style={{ fontWeight: 700, fontSize: 12.5, fontFamily: "'Merriweather', serif" }}>Email:</div>
                {college.email.map((e, i) => <div key={i} style={{ fontSize: 12 }}>{e}</div>)}
              </div>
              <div style={{ height: 12 }}/>
              <div className="text-center">
                <div style={{ fontWeight: 700, fontSize: 12.5, fontFamily: "'Merriweather', serif" }}>Website:</div>
                <div style={{ fontSize: 12 }}>{college.website}</div>
              </div>
              <div style={{ height: 12 }}/>
              <div className="text-center">
                <div style={{ fontWeight: 700, fontSize: 12.5, fontFamily: "'Merriweather', serif" }}>Director:</div>
                <div style={{ fontSize: 12 }}>{college.director}</div>
              </div>
            </div>

            {/* group photo below the contact card, spanning full center column */}
            <div className="mt-4">
              <Photo src={college.photos.group} ratio="16/9" label="Group"/>
            </div>
          </div>

          {/* ============ RIGHT COLUMN ============ */}
          <div>
            <Photo src={college.photos.hall} ratio="4/3" label="Hall"/>
            <Photo src={college.photos.lab} ratio="4/3" label="Lab"/>
            {college.paras_right.map((t, i) => <P key={i}>{t}</P>)}

            {/* Courses offered */}
            <div style={{ marginTop: 8 }}>
              <Heading>Courses Offered:</Heading>
              <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 12.5, color: INK }}>Post Graduated Programmes</div>
              {college.courses_pg.map(c => <div key={c} style={{ fontSize: 12, fontFamily: "'PT Serif', serif" }}>{c}</div>)}
              <div style={{ height: 6 }}/>
              <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 12.5, color: INK }}>Doctoral Program</div>
              {college.courses_doc.map(c => <div key={c} style={{ fontSize: 12, fontFamily: "'PT Serif', serif" }}>{c}</div>)}
            </div>

            <div style={{ marginTop: 12 }}>
              <span style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 12.5, color: INK }}>Facilities for Student: </span>
              <span style={{ fontFamily: "'PT Serif', serif", fontSize: 12, color: INK }}>{college.facilities}</span>
            </div>
          </div>
        </div>

        {/* Bottom paragraph continuation under left column (spans the left column below the group photo area) */}
        <div className="grid md:grid-cols-[1.05fr_1fr_1.05fr] gap-6 mt-4">
          <div>
            {college.paras_left_bottom.map((s, i) => (
              <div key={i}>
                <Heading>{s.heading}</Heading>
                <P>{s.body}</P>
              </div>
            ))}
          </div>
          <div/>
          <div/>
        </div>

        {/* Bottom black bar */}
        <div style={{ height: 1, background: RULE, marginTop: 32 }}/>
        <div className="flex justify-center mt-4" data-testid="footer-bar">
          <div style={{
            background: INK, color: "#FFFFFF",
            padding: "10px 24px",
            fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 14,
            letterSpacing: 1.2,
          }}>
            HED · Higher Education Directory
          </div>
        </div>
        <div style={{ height: 32 }}/>
      </div>
    </div>
  );
}
